import React, { Component } from 'react';
import Select from 'react-select';
import { CircleCheck, CircleX } from 'lucide-react';
import { isTrue } from '../../helpers/isTrue';
import DomainImage from '../../components/DomainImage';

// Solid dark fill for the domain selector: the header already carries a gradient
// behind it, so the control itself must stay flat to read as a proper dark button.
const DARK_CONTROL_BG = '#0e0f13';
// Shared dark panel color for floating menus (matches the top menu dropdowns).
const DARK_PANEL_BG = '#1b1c28';

// Green filled disc with a white check when the domain is active, red disc with a
// white cross when it is not. The disc fill and its ring come from the stylesheet
// (`.domain-status-icon`) so the glyph itself keeps the white stroke.
const domainStatusIcon = (domain) =>
    domain?.active ? (
        <CircleCheck className="domain-status-icon is-active" size={20} color="#32B1A5" />
    ) : (
        <CircleX className="domain-status-icon is-inactive" size={20} color="#fff" />
    );

class SearchDropdown extends Component {
    constructor(props) {
        super();

        this.handleSelect = this.handleSelect.bind(this);
        this.handleChangeVisibility = this.handleChangeVisibility.bind(this);
        this.handleMoodeChange = this.handleMoodeChange.bind(this);

        const domain = JSON.parse(props.domains).find((domain) => domain.id === props.domain_id);
        const isDark = JSON.parse(localStorage.getItem('dark-mode'));

        this.state = {
            value: isTrue(props.is_superuser)
                ? {
                      label: (
                          <div className="domain-select-value">
                              <span className="domain-name">{domain?.url ?? 'Select Domain'}</span>
                              {domainStatusIcon(domain)}
                          </div>
                      ),
                      value: domain?.id,
                      id: domain?.id,
                  }
                : {
                      label: <span className="domain-name">{domain?.url ?? 'Select Domain'}</span>,
                      value: domain?.id,
                      id: domain?.id,
                  },
            isHidden: false,
            isDark: !!isDark,
        };
    }

    componentDidMount() {
        this.handleChangeVisibility();
        document.addEventListener('changeMoodEvent', this.handleMoodeChange);
        document.addEventListener('changeDomainVisibilityEvent', this.handleChangeVisibility);
    }

    componentWillUnmount() {
        document.removeEventListener('changeMoodEvent', this.handleMoodeChange);
        document.removeEventListener('changeDomainVisibilityEvent', this.handleChangeVisibility);
    }

    handleMoodeChange() {
        const isDark = JSON.parse(localStorage.getItem('dark-mode'));
        this.setState({ isDark });
    }

    handleChangeVisibility() {
        const isHidden = JSON.parse(localStorage.getItem('hide-domain'));
        this.setState({ isHidden });
    }

    handleSelect(event) {
        const pathName = window.location.pathname.split('/');
        pathName.splice(2, 1, event.id);
        const newPath = pathName.join('/');
        window.location.pathname = newPath;
    }

    render() {
        // Active domains first, then inactive; each group alphabetical by domain name.
        const domains = JSON.parse(this.props.domains).sort(
            (a, b) =>
                Number(!!b?.active) - Number(!!a?.active) ||
                (a?.name ?? a?.url ?? '').localeCompare(b?.name ?? b?.url ?? ''),
        );

        const optionList = isTrue(this.props.is_superuser)
            ? domains.map((domain) => ({
                  id: domain?.id,
                  value: domain?.url,
                  label: (
                      <div className="gsc-test-user">
                          <div> {domain?.url ?? 'Select Domain'}</div>
                          <div>{domainStatusIcon(domain)}</div>
                      </div>
                  ),
              }))
            : domains.map((domain) => ({
                  value: domain?.id,
                  label: domain?.url,
                  id: domain?.id,
              }));

        return (
            <div className="dropdown border primary no-padding domains-dropdown">
                {this.props.domain_name && (
                    <li className="list-group domain-icon-box">
                        <div className="circle-with-content">
                            <DomainImage domain_name={this.props.domain_name} />
                        </div>
                    </li>
                )}
                <Select
                    options={optionList}
                    defaultValue={this.state.value}
                    className="domains-select"
                    classNamePrefix="domain-select"
                    menuPortalTarget={document.querySelector('body')}
                    menuPosition={'fixed'}
                    styles={{
                        container: (baseStyles) => ({
                            ...baseStyles,
                            width: '100%',
                        }),
                        control: (baseStyles) => ({
                            ...baseStyles,
                            zIndex: 11,
                            minWidth: '295px',
                            backgroundColor: this.state.isDark ? DARK_CONTROL_BG : 'white',
                            borderColor: this.state.isDark ? 'rgba(255, 255, 255, 0.1)' : '#d9d9d9',
                            // Subtle top highlight so the border does not look flat.
                            boxShadow: this.state.isDark
                                ? 'inset 0 1px 0 0 rgba(255, 255, 255, 0.12)'
                                : 'none',
                            paddingLeft: '35px',
                        }),
                        valueContainer: (baseStyles) => ({
                            ...baseStyles,
                            paddingRight: '2px',
                        }),
                        // The domain name and the chevron sit in one seamless button.
                        indicatorSeparator: () => ({
                            display: 'none',
                        }),
                        dropdownIndicator: (baseStyles) => ({
                            ...baseStyles,
                            paddingLeft: '4px',
                            paddingRight: '10px',
                            color: this.state.isDark ? 'rgba(255, 255, 255, 0.7)' : '#98a2b3',
                            '&:hover': {
                                color: this.state.isDark ? '#ffffff' : '#667085',
                            },
                        }),
                        menuPortal: (provided) => ({
                            ...provided,
                            backgroundColor: this.state.isDark ? DARK_PANEL_BG : 'white',
                            zIndex: 10002,
                            WebkitTextSecurity: this.state.isHidden ? 'disc' : 'none',
                        }),
                        menu: (provided) => ({
                            ...provided,
                            backgroundColor: this.state.isDark ? DARK_PANEL_BG : 'white',
                            zIndex: 10002,
                            borderRadius: '8px',
                        }),
                        option: (base, state) => ({
                            ...base,
                            color: state.isSelected
                                ? 'white'
                                : this.state.isDark
                                ? 'white'
                                : 'black',
                            backgroundColor: state.isSelected
                                ? '#33b1a5'
                                : this.state.isDark
                                ? DARK_PANEL_BG
                                : 'white',
                            '&:hover': {
                                backgroundColor: this.state.isDark ? '#2a2c3a' : '#d0d5dd',
                                color: 'white',
                            },
                        }),
                    }}
                    theme={(theme) => ({
                        ...theme,
                        borderRadius: '999px',
                        padding: '8px 12px',
                        borderColor: this.state.isDark ? 'rgba(255, 255, 255, 0.1)' : '#d9d9d9',
                        backgroundColor: this.state.isDark ? DARK_CONTROL_BG : 'white',
                        marginLeft: '40px',
                        zIndex: 11,
                        colors: {
                            ...theme.colors,
                            colors: {
                                ...theme.colors,
                                primary: this.state.isDark ? 'white ' : '#888adb',
                            },
                        },
                    })}
                    onChange={this.handleSelect}
                    isSearchable={true}
                    placeholder="Select domain"
                    onFocus={(e) => {
                        const parent = e.nativeEvent.target.parentNode.parentNode;
                        const selectedValue = parent.children[0];
                        selectedValue.style.display = 'none';
                    }}
                    onBlur={(e) => {
                        const parent = e.nativeEvent.target.parentNode.parentNode;
                        const selectedValue = parent.children[0];
                        selectedValue.style.display = 'inline-grid';
                    }}
                    blurInputOnSelect={true}
                />
            </div>
        );
    }
}

export default SearchDropdown;
